<template>
  <div
    v-if="isShowPreloader"
    class="sport-preloader"
  >
    <dot-lottie-vue
      class="sport-preloader__lottie"
      autoplay
      loop
      :src="`/lottie/loader-${colorMode.value}.lottie`"
      :speed="0.5"
    />

    <div class="sport-preloader__loading-text">
      {{ currentTitle }}
    </div>
    <div class="progress-bar">
      <div
        class="progress-bar__value"
        :style="{ '--progress-width': `${progressWidth}%` }"
      />
    </div>
  </div>
</template>

<script setup>
import { DotLottieVue } from '@lottiefiles/dotlottie-vue';

const DELAY_SHOW_PRELOADER = 1000;

const colorMode = useColorMode();

const { getContent } = useProjectMethods();
const globalStore = useGlobalStore();
const { layoutData, defaultLocaleLayoutData } = storeToRefs(globalStore);

const isShowPreloader = ref(true);
const isSportBookLoaded = ref(false);
const isProgressBarFinished = ref(false);
const randomParts = ref([]);
const progressWidth = ref(0);
const preloaderStartTime = ref(0);

const preloadTitles = computed(() =>
  getContent(layoutData.value, defaultLocaleLayoutData.value, 'preloader.sportPreloader.titles'),
);
const currentTitle = ref(preloadTitles.value[0]);

const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const setUniqueRandomNumbers = (min, max, count) => {
  const uniqueNumbers = new Set();

  while (uniqueNumbers.size < count) {
    const randomNum = getRandomNumber(min, max);
    uniqueNumbers.add(randomNum);
  }

  randomParts.value = [...Array.from(uniqueNumbers).sort((a, b) => a - b), 95];
};

const renderProgress = () => {
  randomParts.value.forEach((part, index) => {
    setTimeout(
      () => {
        progressWidth.value = part;
        currentTitle.value = preloadTitles.value[index + 1] || currentTitle.value;

        if (index === randomParts.value.length - 1) {
          isProgressBarFinished.value = true;
        }
      },
      part * index * 4,
    );
  });
};

const handleSBLoaded = () => {
  isSportBookLoaded.value = true;
};

defineExpose({ handleSBLoaded });

onBeforeMount(() => {
  setUniqueRandomNumbers(0, 90, 4);
  preloaderStartTime.value = Date.now();
});

onMounted(async () => {
  await nextTick();
  renderProgress();
});

watch([isSportBookLoaded, isProgressBarFinished], ([newIsSportBookLoaded, newIsProgressBarFinished]) => {
  if (newIsSportBookLoaded && newIsProgressBarFinished) {
    const currentTime = Date.now();
    const elapsedTime = currentTime - preloaderStartTime.value;
    const remainingTime = Math.max(0, DELAY_SHOW_PRELOADER - elapsedTime);

    setTimeout(() => {
      isShowPreloader.value = false;
    }, remainingTime);
  }
});
</script>

<style src="~/assets/styles/components/atomic/sport-preloader.scss" lang="scss" />
